import axios from "axios";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { apiConfig } from "../../config";
import { SubscriptionInvoice } from "../../types/billing";

class InvoiceClient {
  private auth0Client: Auth0Client;

  constructor(auth0Client: Auth0Client) {
    this.auth0Client = auth0Client;
  }

  private async getAuthHeaders() {
    const token = await this.auth0Client.getTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Get recurring invoices for current billing cycle only (limited, correct data for subscription view).
   */
  async getInvoices(orgId: string): Promise<SubscriptionInvoice[]> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/invoices/org/${orgId}`,
      { headers },
    );
    return response.data;
  }

  /**
   * Get invoices for org with optional billing cycle filter (for Billing History page).
   * If cycleMonth and cycleYear are provided, only invoices for that month are returned; otherwise all for org.
   */
  async getInvoicesByCycle(
    orgId: string,
    options?: { cycleMonth: number; cycleYear: number },
  ): Promise<SubscriptionInvoice[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams();
    if (options?.cycleMonth != null)
      params.set("cycleMonth", String(options.cycleMonth));
    if (options?.cycleYear != null)
      params.set("cycleYear", String(options.cycleYear));
    const query = params.toString();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/invoices/org/${orgId}/cycle${query ? `?${query}` : ""}`,
      { headers },
    );
    return response.data;
  }

  async getInvoice(invoiceId: string): Promise<SubscriptionInvoice> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/invoices/invoice/${invoiceId}`,
      { headers },
    );
    return response.data;
  }

  async getInvoiceByLedgerId(ledgerId: string): Promise<SubscriptionInvoice> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/invoices/ledger/${ledgerId}`,
      { headers },
    );
    return response.data;
  }

  async getBillingHistory(
    orgId: string,
    includeCancelled: boolean = true,
  ): Promise<unknown[]> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/invoices/billing-history/${orgId}?includeCancelled=${includeCancelled}`,
      { headers },
    );
    return response.data;
  }

  async contestInvoice(
    invoiceId: string,
    reason: string,
    options?: { orgAuthId?: string; contestedBy?: string },
  ): Promise<SubscriptionInvoice> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/invoices/invoice/${invoiceId}/contest`,
      { reason, ...options },
      { headers },
    );
    return response.data;
  }
}

export default InvoiceClient;
